import { useState } from "react";
import { Link } from "react-router-dom";

import { enterpriseContext, type ConceptPreview } from "../api/resources";
import { RegistryTable } from "../components/RegistryTable";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  PageHeader,
  Tabs,
} from "../components/ui";
import { AssistantPanel } from "../components/assistant/AssistantPanel";
import { useAuth } from "../lib/AuthContext";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";

/**
 * Context preview: what Enterprise Context has published beside what this company's people told
 * us — per concept, side by side, never blended. Survey does not author semantic truth here; it
 * shows the comparison and offers to ask someone or propose a correction.
 */
const SENSITIVITY = ["public", "internal", "confidential", "restricted", "pii"];

function kindLabel(kind: string | null, field: string | null): string {
  const base: Record<string, string> = {
    term: "what it is called",
    authority_rule: field
      ? `who owns ${field.replace(/_/g, " ")}`
      : "system of record",
    provider_mapping: "what a system calls it",
    ownership: "who owns it",
    relationship: "how it relates",
    entity_definition: "what it means",
    field_definition: "what a field means",
    policy_definition: "a rule",
  };
  return base[kind ?? ""] ?? kind ?? "";
}

export function ContextPreviewPage() {
  const { organizationId } = useCurrentOrganization();
  const { can } = useAuth();
  const [askOpen, setAskOpen] = useState(false);
  const [tab, setTab] = useState<"concepts" | "entities" | "fields" | "rules">(
    "concepts",
  );
  const [error, setError] = useState<string | undefined>();
  const [asked, setAsked] = useState<string | undefined>();
  const preview = useAsync(
    () =>
      organizationId
        ? enterpriseContext.preview(organizationId)
        : Promise.resolve([]),
    [organizationId],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  async function ask(concept: string) {
    setError(undefined);
    try {
      await enterpriseContext.ask(organizationId!, concept);
      setAsked(concept);
      preview.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <>
      <PageHeader
        title="Context preview"
        description="What Enterprise Context has published, next to what this company's people told us. Published is official; everything else is an observation until a steward decides."
        actions={
          <button type="button" className="btn" onClick={() => setAskOpen((open) => !open)}>
            {askOpen ? "Hide assistant" : "Ask about this context"}
          </button>
        }
      />

      {askOpen && organizationId ? (
        <div style={{ height: 420 }}>
          <AssistantPanel
            organizationId={organizationId}
            mode="context_assistant"
            operation="context_explanation"
            title="Assistant — about this context"
            suggestions={[
              "Why does Survey say one thing while Enterprise Context says another?",
              "What is published for this concept?",
              "What have people told us that is not published?",
            ]}
            onClose={() => setAskOpen(false)}
          />
        </div>
      ) : null}

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "concepts", label: "Published vs heard" },
          { value: "entities", label: "Local notes: entities" },
          { value: "fields", label: "Local notes: fields" },
          { value: "rules", label: "Local notes: rules" },
        ]}
      />

      {tab === "concepts" ? (
        <div className="stack">
          <ErrorBanner error={error ?? preview.error} />
          {asked ? (
            <div className="banner banner--ok">
              A task was created to ask about {asked}.
            </div>
          ) : null}
          {preview.loading ? (
            <Loading />
          ) : (preview.data ?? []).length === 0 ? (
            <Card>
              <EmptyState
                title="No concepts to compare yet"
                description="Concepts appear here once questions declare an Enterprise Context target. Refresh Enterprise Context in Settings to load what is published."
              />
            </Card>
          ) : (
            (preview.data ?? []).map((c) => (
              <ConceptCard
                key={c.concept}
                c={c}
                canAsk={can(organizationId, "tasks:assign")}
                onAsk={() => void ask(c.concept)}
              />
            ))
          )}
        </div>
      ) : null}

      {tab === "entities" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="entities"
          emptyTitle="No local entity notes"
          emptyDescription="Notes about business objects as people describe them here. Governed definitions live in Enterprise Context."
          columns={[
            { key: "canonical_name", label: "Entity (as said here)" },
            { key: "aliases", label: "Aliases heard" },
            { key: "definition", label: "Definition heard", kind: "text" },
            {
              key: "verification_status_code",
              label: "Confirmation",
              kind: "status",
            },
          ]}
          createFields={[
            {
              key: "canonical_name",
              label: "Name as people say it",
              required: true,
            },
            { key: "definition", label: "Definition as people describe it" },
          ]}
        />
      ) : null}

      {tab === "fields" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="data-fields"
          emptyTitle="No local field notes"
          emptyDescription="Field-level detail heard in interviews. Technical schemas belong to DataHub; governed field meaning to Enterprise Context."
          columns={[
            { key: "name", label: "Field" },
            { key: "canonical_name", label: "Canonical name", kind: "text" },
            { key: "data_type", label: "Type" },
            { key: "source_field", label: "Source field", kind: "text" },
            { key: "sensitivity", label: "Sensitivity" },
            {
              key: "verification_status_code",
              label: "Confirmation",
              kind: "status",
            },
          ]}
          createFields={[
            { key: "entity_id", label: "Entity ID", required: true },
            { key: "name", label: "Field name", required: true },
            { key: "canonical_name", label: "Canonical name" },
            { key: "definition", label: "Definition" },
            { key: "data_type", label: "Data type" },
            { key: "source_field", label: "Source field" },
            { key: "example_value", label: "Example" },
            { key: "sensitivity", label: "Sensitivity", options: SENSITIVITY },
          ]}
        />
      ) : null}

      {tab === "rules" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="business-rules"
          emptyTitle="No business rules recorded"
          emptyDescription="Tribal-knowledge interviews capture the rules employees remember but no system enforces."
          columns={[
            { key: "name", label: "Rule" },
            { key: "rule_text", label: "Statement", kind: "text" },
            { key: "scope", label: "Scope" },
            { key: "is_documented", label: "Documented" },
            {
              key: "verification_status_code",
              label: "Confirmation",
              kind: "status",
            },
          ]}
          createFields={[
            { key: "name", label: "Name", required: true },
            { key: "rule_text", label: "Rule text", required: true },
            { key: "scope", label: "Scope" },
          ]}
        />
      ) : null}
    </>
  );
}

function ConceptCard({
  c,
  canAsk,
  onAsk,
}: {
  c: ConceptPreview;
  canAsk: boolean;
  onAsk: () => void;
}) {
  return (
    <Card
      title={`${c.preferred_label} — ${c.concept}`}
      actions={
        <div className="row" style={{ gap: 6 }}>
          {canAsk ? (
            <button className="btn--small" onClick={onAsk}>
              Ask someone
            </button>
          ) : null}
          <Link className="btn btn--small" to="/knowledge">
            Propose correction
          </Link>
          <Link className="btn btn--small" to="/knowledge">
            View evidence
          </Link>
        </div>
      }
    >
      <div className="grid grid--two" data-testid={`concept-${c.concept}`}>
        <div>
          <h3>Published in Enterprise Context</h3>
          {c.published.length === 0 ? (
            <p className="small muted">
              Nothing published for this concept yet.
            </p>
          ) : (
            <ul className="small">
              {c.published.map((p) => (
                <li key={p.definition_id}>
                  <span className="badge badge--verified">Published</span>{" "}
                  {kindLabel(p.kind, p.field)}:{" "}
                  <strong>{p.value ?? p.subject_key}</strong>{" "}
                  <span className="muted">v{p.version}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3>What we heard here</h3>
          {c.heard.length === 0 ? (
            <p className="small muted">
              Not asked yet
              {c.open_task_count ? ` — ${c.open_task_count} open task` : ""}.
            </p>
          ) : (
            <ul className="small">
              {c.heard.map((h) => (
                <li key={h.fact_id}>
                  <span
                    className={`badge badge--${h.human_confirmed ? "state-verified" : "state-needs_confirmation"}`}
                  >
                    {h.human_confirmed ? "Human confirmed" : "Heard"}
                  </span>{" "}
                  {kindLabel(h.kind, h.field)}: <strong>{h.value}</strong>{" "}
                  <span
                    className="badge badge--neutral"
                    title="What Enterprise Context did with it"
                  >
                    {h.governance_state}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {c.differences.length > 0 ? (
        <div className="banner banner--warn" data-testid="differences">
          <strong>Differences.</strong>{" "}
          {c.differences.map((d) => (
            <div key={`${d.fact_id}-${d.definition_id}`}>
              {kindLabel(d.kind, d.field)}: published{" "}
              <strong>{d.published}</strong>, but a person here confirmed{" "}
              <strong>{d.confirmed}</strong>. Nobody resolves this automatically
              — ask someone or propose a correction.
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
